import { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE } from 'config/api';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check if user is logged in on mount
        // Prefer admin token over user token
        const checkAuth = async () => {
            const adminToken = localStorage.getItem('admin_token');
            const userToken = localStorage.getItem('user_token');

            if (adminToken) {
                const adminUser = localStorage.getItem('admin_user');
                if (adminUser) {
                    setToken(adminToken);
                    setUser(JSON.parse(adminUser));
                }
            } else if (userToken) {
                const regularUser = localStorage.getItem('user');
                if (regularUser) {
                    setToken(userToken);
                    setUser(JSON.parse(regularUser));
                }
            }
            setLoading(false);
        };

        checkAuth();
    }, []);

    // Admin login
    const login = async (email, password) => {

        try {
            const response = await fetch(`${API_BASE}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password, admin: true }),
            });

            const data = await response.json();

            if (response.ok) {
                localStorage.setItem('admin_token', data.token);
                localStorage.setItem('admin_user', JSON.stringify(data.user));
                setToken(data.token);
                setUser(data.user);
                return { success: true };
            } else {
                return { success: false, message: data.message || 'Login failed' };
            }
        } catch (error) {
            return { success: false, message: 'Network error. Please try again.' };
        }
    };

    // User login
    const userLogin = async (email, password) => {
        try {
            const response = await fetch(`${API_BASE}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (response.ok) {
                if (data.user?.role === 'admin') {
                    localStorage.removeItem('user_token');
                    localStorage.removeItem('user');
                    localStorage.setItem('admin_token', data.token);
                    localStorage.setItem('admin_user', JSON.stringify(data.user));
                    setToken(data.token);
                    setUser(data.user);
                    return { success: true };
                }

                localStorage.setItem('user_token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                setToken(data.token);
                setUser(data.user);
                return { success: true };
            } else {
                return { success: false, message: data.message || 'Login failed' };
            }
        } catch (error) {
            return { success: false, message: 'Network error. Please try again.' };
        }
    };

    // User registration
    const userRegister = async (name, email, password) => {
        try {
            const response = await fetch(`${API_BASE}/auth/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ name, email, password }),
            });

            const data = await response.json();

            if (response.ok) {
                localStorage.setItem('user_token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                setToken(data.token);
                setUser(data.user);
                return { success: true };
            } else {
                return { success: false, message: data.message || 'Registration failed' };
            }
        } catch (error) {
            return { success: false, message: 'Network error. Please try again.' };
        }
    };

    const logout = () => {
        localStorage.removeItem('user_token');
        localStorage.removeItem('admin_token');
        localStorage.removeItem('user');
        localStorage.removeItem('admin_user');
        setToken(null);
        setUser(null);
    };

    const apiCall = async (endpoint, options = {}) => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            ...options.headers,
        };
        if (!(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        const response = await fetch(`${API_BASE}${endpoint}`, {
            ...options,
            headers,
        });

        if (response.status === 401) {
            logout();
            throw new Error('Session expired');
        }

        return response;
    };

    const value = {
        user,
        token,
        loading,
        login,
        userLogin,
        userRegister,
        logout,
        apiCall,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin' && !!localStorage.getItem('admin_token'),
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
